import GtfsRealtimeBindings from "gtfs-realtime-bindings";
import { apiGetRaw } from "./api-client";

export type RealtimeMode = "sydneytrains" | "metro" | "innerwest" | "buses" | "ferries" | "lightrail" | "nswtrains";

export interface RealtimeVehiclePosition { trip_id: string; route_id: string; start_date: string; latitude: number; longitude: number; bearing?: number; speed?: number; vehicle_id?: string; vehicle_label?: string; stop_id?: string; timestamp: number; }
export interface RealtimeAlert { id: string; header_text: string; description_text: string; active_periods: { start: number; end: number }[]; informed_routes: string[]; informed_stops: string[]; cause?: string; effect?: string; }

function s(v: any): string { return v == null ? "" : typeof v === "string" ? v : String(v); }
function n(v: any): number { if (typeof v === "number") return v; if (v && typeof v.low === "number") return v.low; if (v && typeof v.toNumber === "function") return v.toNumber(); return Number(v) || 0; }
function arr(v: any): any[] { return Array.isArray(v) ? v : v ? [v] : []; }

const VEHICLE_CACHE_TTL_MS = 5_000;
const vehicleCache = new Map<RealtimeMode, { at: number; data: RealtimeVehiclePosition[] }>();
const vehicleInFlight = new Map<RealtimeMode, Promise<RealtimeVehiclePosition[]>>();

export async function fetchVehiclePositions(mode: RealtimeMode): Promise<RealtimeVehiclePosition[]> {
  const cached = vehicleCache.get(mode);
  if (cached && Date.now() - cached.at < VEHICLE_CACHE_TTL_MS) return cached.data;

  const pending = vehicleInFlight.get(mode);
  if (pending) return pending;

  const request = (async () => {
    const buffer = await apiGetRaw("/gtfs/vehiclepos/" + mode);
    const feed = GtfsRealtimeBindings.transit_realtime.FeedMessage.decode(new Uint8Array(buffer));
    const json = feed.toJSON();
    const positions = arr(json.entity).filter((e: any) => e.vehicle).map((e: any) => (({
      trip_id: s(e.vehicle.trip?.tripId), route_id: s(e.vehicle.trip?.routeId), start_date: s(e.vehicle.trip?.startDate),
      latitude: n(e.vehicle.position?.latitude), longitude: n(e.vehicle.position?.longitude),
      bearing: e.vehicle.position?.bearing != null ? n(e.vehicle.position.bearing) : undefined,
      speed: e.vehicle.position?.speed != null ? n(e.vehicle.position.speed) : undefined,
      vehicle_id: e.vehicle.vehicle?.id ? s(e.vehicle.vehicle.id) : undefined,
      vehicle_label: e.vehicle.vehicle?.label ? s(e.vehicle.vehicle.label) : undefined,
      stop_id: e.vehicle.stopId ? s(e.vehicle.stopId) : undefined,
      timestamp: n(e.vehicle.timestamp)
    })));
    vehicleCache.set(mode, { at: Date.now(), data: positions });
    return positions;
  })();

  vehicleInFlight.set(mode, request);
  try {
    return await request;
  } finally {
    vehicleInFlight.delete(mode);
  }
}

function stripHtml(text: string): string {
  if (!text) return '';
  return text.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
}

export async function fetchAlerts(mode: string = "all"): Promise<RealtimeAlert[]> {
  const buffer = await apiGetRaw("/gtfs/alerts/" + mode);
  const feed = GtfsRealtimeBindings.transit_realtime.FeedMessage.decode(new Uint8Array(buffer));
  const json = feed.toJSON();
  return arr(json.entity).filter((e: any) => e.alert).map((e: any) => {
    const a = e.alert;
    const headerText = stripHtml(arr(a.headerText?.translation).map((t: any) => t.text).join(" "));
    const descText = stripHtml(arr(a.descriptionText?.translation).map((t: any) => t.text).join(" "));
    const periods = arr(a.activePeriod).map((p: any) => ({ start: n(p.start), end: n(p.end) }));
    const entities = arr(a.informedEntity);
    return {
      id: s(e.id), header_text: headerText, description_text: descText, active_periods: periods,
      informed_routes: [...new Set(entities.filter((x: any) => x.routeId).map((x: any) => s(x.routeId)))],
      informed_stops: [...new Set(entities.filter((x: any) => x.stopId).map((x: any) => s(x.stopId)))],
      cause: s(a.cause),
      effect: s(a.effect),
    };
  });
}
